/**
 * The enabled-locale list, published to the browser as a cookie.
 *
 * Most of the app reads locale availability from React context
 * (`LocaleAvailabilityProvider`), which is seeded server-side inside
 * `[locale]/layout.tsx`. But a few call sites need the list *outside* that
 * tree and outside React entirely:
 *
 *   - `/auth/callback` and `/auth/logout` live outside `app/[locale]`, so no
 *     locale layout has rendered by the time they pick a locale to land on.
 *   - `resolveClientLocale()` in `lib/helpers.ts` is called from plain modules
 *     (API interceptors), where hooks aren't available.
 *
 * `proxy.ts` already resolves availability on every request, so it writes the
 * list to a readable cookie on the way out — the same trick the file uses for
 * `x-user-country`. Anything that can't reach the context reads the cookie and
 * falls back to the built-in list if it's absent.
 *
 * This is a convenience for choosing *which* locale to route someone to, not
 * an authorization boundary: a tampered cookie at worst lands the visitor on
 * a locale-prefixed URL that `proxy.ts` then rewrites back to a real one.
 */

import {
  CATALOG_LOCALE_VALUES,
  FALLBACK_ENABLED_LOCALES,
} from "@/lib/constants/locale";

export const ENABLED_LOCALES_COOKIE = "enabled-locales";

/** Serializes for the cookie value. Comma-joined; locale tags never contain commas. */
export const serializeEnabledLocales = (values: string[]): string =>
  values.join(",");

/**
 * Parses the cookie value, dropping anything the running image has no catalog
 * for — a cookie can outlive a rollback that removed a locale.
 */
export const parseEnabledLocales = (raw: string | undefined | null): string[] =>
  (raw ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => CATALOG_LOCALE_VALUES.has(value));

/**
 * Enabled locales as seen from the browser. Returns the built-in fallback list
 * when the cookie is missing, unreadable, or empty — never an empty array, so
 * callers can treat it as a non-empty allowlist.
 */
export function getEnabledLocalesFromCookie(): string[] {
  if (typeof document === "undefined") return FALLBACK_ENABLED_LOCALES;

  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${ENABLED_LOCALES_COOKIE}=`));

  const parsed = parseEnabledLocales(
    match
      ? decodeURIComponent(match.slice(ENABLED_LOCALES_COOKIE.length + 1))
      : "",
  );

  return parsed.length > 0 ? parsed : FALLBACK_ENABLED_LOCALES;
}

/** True when `value` is a locale this environment currently offers. */
export const isEnabledLocale = (value?: string | null): boolean =>
  !!value && getEnabledLocalesFromCookie().includes(value);
